// Cache produit par EAN (objectif § 4 : « l'API est publique et lente par moments... un EAN ne
// change pas »). Cache navigateur uniquement : pas de base de données côté `api/` à ce stade du
// dépôt (CLAUDE.md, `infra/` ne provisionne que du Blob Storage) — rien à mettre en cache côté API
// tant qu'aucune base n'existe.
//
// Un produit trouvé est mis en cache indéfiniment (un EAN ne change pas). Une fiche `incomplete`
// (existe mais `nutriments` inexploitable) et un EAN `not-found` sont mis en cache avec une durée de
// vie courte : la fiche peut être complétée, ou créée, après notre premier essai (y compris via le
// lien de contribution qu'on propose nous-mêmes). Les trois statuts sont distincts en cache — un
// produit `incomplete` ne doit jamais redevenir `not-found` au scan suivant (perdre le nom affiché
// et laisser croire que la fiche n'existe pas du tout inciterait à créer un doublon sur OFF).
import type { OffProduct } from './types.js';

const STORAGE_KEY_PREFIX = 'nutriscan:off-cache:v2:';
const TRANSIENT_TTL_MS = 24 * 60 * 60 * 1000;

/** Ce qu'on garde en cache pour un EAN — miroir de `OffLookupResult` sans les champs recalculables. */
export type CachedProductLookup =
  | { status: 'found'; product: OffProduct }
  | { status: 'incomplete'; productName: string | null }
  | { status: 'not-found' };

interface StoredEntry {
  entry: CachedProductLookup;
  cachedAt: number;
}

export interface ProductCache {
  /** `undefined` = pas en cache, il faut appeler le réseau. */
  get(ean: string): CachedProductLookup | undefined;
  set(ean: string, entry: CachedProductLookup): void;
}

function safeLocalStorage(): Storage | undefined {
  try {
    return typeof window !== 'undefined' ? window.localStorage : undefined;
  } catch {
    // Safari en navigation privée peut lever à l'accès de `localStorage`, pas seulement à l'usage.
    return undefined;
  }
}

/** Cache en mémoire, doublé d'un `localStorage` quand disponible (survit à un rechargement). */
export function createProductCache(storage: Storage | undefined = safeLocalStorage()): ProductCache {
  const memory = new Map<string, StoredEntry>();

  function isFresh(stored: StoredEntry): boolean {
    return stored.entry.status === 'found' || Date.now() - stored.cachedAt < TRANSIENT_TTL_MS;
  }

  function readFromStorage(ean: string): StoredEntry | undefined {
    if (!storage) return undefined;
    try {
      const raw = storage.getItem(STORAGE_KEY_PREFIX + ean);
      if (raw === null) return undefined;
      return JSON.parse(raw) as StoredEntry;
    } catch {
      return undefined;
    }
  }

  return {
    get(ean) {
      const stored = memory.get(ean) ?? readFromStorage(ean);
      if (!stored || !isFresh(stored)) return undefined;
      memory.set(ean, stored);
      return stored.entry;
    },
    set(ean, entry) {
      const stored: StoredEntry = { entry, cachedAt: Date.now() };
      memory.set(ean, stored);
      if (!storage) return;
      try {
        storage.setItem(STORAGE_KEY_PREFIX + ean, JSON.stringify(stored));
      } catch {
        // Quota dépassé ou stockage indisponible : le cache mémoire suffit pour le reste de la session.
      }
    },
  };
}
